/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSelectV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { componentStyle } from "../../../../config/appTheme";
import CustomToolTip from "../../../infoButton/CustomToolTip";

function CustomSelectV2({
  heightExpand = false,
  size = "small",
  label = "Select",
  variant = "outlined",
  required = true,
  disabled,
  optionsList = [],
  selectedValue,
  formControlSx,
  labelSx,
  selectSx,
  menuItemSx,
  sltFldStyle = {},
  handleOnChange,
  enableSearch = false,
  showLabel = true,
  dark = false,
  placeholder = true,
  showlabelNew = true,
  tag = "Details",
  enableLoading = false,
  disablePortal = true,
  textLength = 0,
}) {
  const [value, setValue] = useState(selectedValue || "");
  const [searchText, setSearchText] = useState("");
  const selectRef = useRef(null);

  const getOptionText = (option) =>
    [option?.label, option?.version]
      .filter((item) => item !== null && item !== undefined)
      .join(" ")
      .toLowerCase();

  const searchableOptions = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    if (!normalizedSearchText) {
      return optionsList || [];
    }

    const filteredOptions = (optionsList || []).filter((option) =>
      getOptionText(option).includes(normalizedSearchText),
    );

    console.log("---filteredOptions---", filteredOptions);
    return filteredOptions;
  }, [optionsList, searchText]);

  useEffect(() => {
    setValue(selectedValue);
  }, [selectedValue]);

  const renderSelect = () => (
    <FormControl
      variant={variant}
      fullWidth
      size={size}
      required={required}
      disabled={disabled}
      sx={{
        ...componentStyle.form.select.formControl,
        ...(dark && { ...componentStyle.form.select.formControlDarkTheme }),
      }}
    >
      {showLabel && (
        <Typography
          shrink
          textAlign="start"
          htmlFor="select-field"
          sx={{
            mb: 0.5,
            ...componentStyle.form.select.label,
            ...(dark && { ...componentStyle.form.select.labelDarkTheme }),
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}>*</span>}
        </Typography>
      )}

      <Select
        id="select-field"
        value={value}
        ref={selectRef}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 250,
              width: selectRef.current?.offsetWidth || "auto",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e0e0e0",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555",
              },
            },
          },
        }}
        onChange={(e) => {
          setValue(e.target.value);
          handleOnChange?.(e);
        }}
        endAdornment={
          enableLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null
        }
        displayEmpty
        sx={{
          ...componentStyle.form.select.select,
          ...(dark && { ...componentStyle.form.select.selectDarkTheme }),
          display: "flex",
          alignItems: "center",
          textAlign: "left",
          "& .MuiSelect-select": {
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          },
        }}
        renderValue={(selected) => {
          if (selected === "" || selected === null || selected === undefined) {
            if (!placeholder) return null;

            const placeholderText = `${label?.replace("*", "") || "option"}`;
            return (
              <Typography sx={{ color: "#999", textAlign: "left" }}>
                {placeholderText}
              </Typography>
            );
          }

          const selectedOption = optionsList?.find(
            (opt) => opt.value === selected,
          );

          return (
            <Box
              sx={[
                sltFldStyle,
                {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  width: "100%",
                },
              ]}
            >
              {selectedOption?.icon && (
                <Box
                  component="img"
                  src={selectedOption.icon}
                  alt={selectedOption.label}
                  sx={{ width: 20, height: 20, mr: 1 }}
                />
              )}
              {textLength > 0 && typeof selectedOption?.label === "string" ? (
                <CustomToolTip
                  length={textLength}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    maxWidth: "100%",
                  }}
                >
                  {selectedOption?.label || selected}
                </CustomToolTip>
              ) : (
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {selectedOption?.label || selected}
                </Box>
              )}
            </Box>
          );
        }}
      >
        {optionsList?.length === 0 ? (
          <MenuItem disabled>
            <Typography sx={{ color: "#999" }}>{`No ${tag} Found`}</Typography>
          </MenuItem>
        ) : (
          optionsList?.map((option, index) => (
            <MenuItem
              key={`${option.value ?? option.label ?? "option"}-${index}`}
              value={option.value}
              disabled={option.disabled}
              sx={{
                ...componentStyle.form.select.menuItem,
                ...(dark && {
                  ...componentStyle.form.select.menuItemDartTheme,
                }),

                "&.Mui-selected": {
                  backgroundColor: "#ebebeb !important",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#e0e0e0 !important",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {option.icon && (
                  <Box
                    component="img"
                    src={option.icon}
                    alt={option.label}
                    sx={{ width: 20, height: 20, mr: 1 }}
                  />
                )}
                <Box flex={1}>{option.label}</Box>
                {label === option.label && option.version && (
                  <Typography
                    variant="body2"
                    sx={{ ml: 2, color: "text.secondary", fontStyle: "italic" }}
                  >
                    v{option.version}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );

  const renderSearchableSelect = () => (
    <Box sx={{ width: "100%" }}>
      {showlabelNew && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            fontSize: "0.9rem",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}
      <Autocomplete
        fullWidth
        disablePortal={disablePortal}
        options={searchableOptions}
        getOptionLabel={(option) =>
          option?.label !== null && option?.label !== undefined
            ? String(option.label)
            : ""
        }
        isOptionEqualToValue={(option, selectedOption) =>
          option?.value === selectedOption?.value
        }
        filterOptions={(options) => options}
        noOptionsText={`No ${tag} Found`}
        value={optionsList?.find((opt) => opt?.value === value) || null}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            setSearchText(newInputValue);
          }

          if (reason === "reset") {
            setSearchText("");
          }
        }}
        onChange={(event, newValue, reason) => {
          const newVal = newValue ? newValue.value : "";

          setValue(newVal);
          handleOnChange?.({ target: { value: newVal } });
        }}

        disabled={disabled}
        sx={{
          ...(dark ? style.autoCompleteDark : style.autoCompleteLight),
          width: "100%",
          maxWidth: "100%",
        }}
        ListboxProps={{
          ...style.listBoxLight,
          sx: {
            ...style.listBoxLight.sx,
            width: "100%",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={variant}
            size={size}
            required={required}
            {...(placeholder && {
              placeholder: `Select ${label?.replace("*", "") || "option"}`,
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {enableLoading ? (
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              ...(heightExpand
                ? {
                  fontWeight: 500,
                  "& .MuiInputLabel-root": {
                    position: "relative",
                    transform: "none",
                    marginBottom: "4px",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-error": { color: "#e53935" },
                    "&.Mui-disabled": { color: "#bdbdbd" },
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    backgroundColor: "#fff",
                    height: "40px", // ✅ set total height
                    "& fieldset": {
                      borderColor: "#d9d9d9",
                      fontWeight: 500, // default
                    },
                    "&:hover .MuiInputBase-input::placeholder": {
                      color: "#0f1011",
                      fontWeight: 500, // dark text color on hover
                    },

                    "&.Mui-focused .MuiInputBase-input::placeholder": {
                      color: "#8c8c8c",
                      fontWeight: 500, // solid black when focused
                    },
                    "&:hover fieldset": {
                      borderColor: "#8c8c8c", // hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#000000", // focused
                    },
                    "&.Mui-disabled fieldset": {
                      borderColor: "#e0e0e0", // disabled
                    },
                    "&.Mui-error fieldset": {
                      borderColor: "#e53935", // error
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#FFF3B0 !important", // light yellow
                    },
                    "& .MuiSelect-select": {
                      paddingY: "10px",
                      fontSize: "12px",
                      "&::placeholder": {
                        color: "#0f0909",
                        fontWeight: 500,
                      },
                      "&:hover::placeholder": {
                        color: "#848587",
                        fontWeight: 500, // dark text on hover
                      },
                      "&.Mui-focused::placeholder": {
                        color: "#232323",
                        fontWeight: 500, // black when focused
                      },
                    },
                  },
                  "& .MuiFormHelperText-root.Mui-error": {
                    color: "#e53935",
                    fontSize: "11px",
                    marginLeft: 0,
                    marginTop: "4px",
                  },
                }
                : componentStyle.form.select.formControl),
              input: { color: "#333" },
            }}
          />
        )}
        renderOption={(props, option, state) => {
          const { key, ...optionProps } = props;

          return (
            <Box
              component="li"
              key={`${option?.value ?? option?.label ?? "option"}-${state.index}`}
              {...optionProps}
              sx={{
                width: "100%",
                cursor: "pointer",
                borderRadius: 0,

                "&[aria-selected='true']": {
                  backgroundColor: "#dfdedeff !important",
                  width: "100%",
                  borderRadius: 0,
                },

                "&:hover": {
                  borderRadius: 0,
                },
              }}
            >
              {option.icon && (
                <Box
                  component="img"
                  src={option.icon}
                  alt={option.label}
                  sx={{ width: 20, height: 20, mr: 1 }}
                />
              )}

              <Box>{option.label}</Box>

              {option.version && (
                <Typography
                  variant="body2"
                  sx={{ ml: 2, color: "text.secondary", fontStyle: "italic" }}
                >
                  {option.version}
                </Typography>
              )}
            </Box>
          );
        }}
      />
    </Box>
  );

  return enableSearch ? renderSearchableSelect() : renderSelect();
}

export default CustomSelectV2;

const style = {
  autoCompleteLight: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
    },
    "& .MuiInputBase-input": {
      fontSize: 14,
    },
  },

  autoCompleteDark: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      backgroundColor: "transparent !important",
      color: "white",

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "white",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "white",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "white !important",
      },

      "& .MuiSvgIcon-root": {
        color: "white",
      },
    },

    "& .MuiInputBase-input": {
      fontSize: 14,
      color: "white",
    },

    "& .MuiInputBase-input::placeholder": {
      color: "white",
      opacity: 0.7,
    },
  },
  listBoxLight: {
    sx: {
      maxHeight: 240,
      overflowY: "auto",
      padding: 0, // space inside the dropdown
      scrollbarWidth: "thin",
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#c1c1c1",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#a0a0a0",
      },
      "& li": {
        marginBottom: "6px", // ✅ Adds visible space between items
        borderRadius: "6px", // for rounded yellow background
      },
      "& li:last-child": {
        marginBottom: 0, // remove extra space at end
      },
    },
  },
};
